/**
 * JobCrawls Plus, My Jobs area (TEC-744): shapes shared by the tracker extras stored on the
 * application user-state items, the /api/plus/myjobs/* responses and the My Jobs UI.
 * Spec: docs/premium-seeker-spec.md 6.2, 6.5, 6.12, 6.21, 6.24, 6.26 and section 11.
 *
 * The tracker extras live on the existing `appliedJob#` item as ONE additive field, `plusTracker`,
 * so the sync layer (buildJobUserStatePayload, extractJobsFromUserState) only needs to carry a
 * single key and older clients that ignore it keep working.
 */
import type { PlusBasis } from './plus';
import type { ApplicationMailNotRead } from './application-mail';
import type { ContractFacts, FixedTermComparison } from '../utils/plus-job-signals/fixed-term';

export type PlusLocale = 'en' | 'fi' | 'sv';

/** One interview (a first round, a technical round...). */
export interface PlusTrackerInterview {
  id: string;
  /** ISO date-time (with offset or Z). */
  at: string;
  place?: string;
  /** Minutes before `at` to remind. Absent: no reminder. */
  reminderMinutes?: number;
  note?: string;
  /** Distance from home in km, worked out by the server when the place was saved (INT-7). */
  distanceKm?: number;
  /** `route`: Digitransit routing distance. `estimate`: straight line x 1.3. */
  distanceSource?: 'route' | 'estimate';
  /** The place text the distance was worked out for; a different place makes the distance stale. */
  distancePlace?: string;
}

/**
 * An interview date the Applying area's ingest wrote from an employer email onto the application
 * (`interviewDates` on the `appliedJob#` entry). Read-only here; carried through client rewrites.
 */
export interface MailInterviewDate {
  /** The employer message id it came from. */
  id: string;
  /** ISO instant when the time is known, else the date alone (YYYY-MM-DD). */
  at: string;
  place?: string;
  link?: string;
  /** See PlusTrackerInterview: stamped when the invite is applied. */
  distanceKm?: number;
  distanceSource?: 'route' | 'estimate';
  distancePlace?: string;
}

export interface PlusTrackerContact {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

/** A dated private note (the free tracker keeps its single `notes` text; these feed the timeline). */
export interface PlusTrackerNote {
  id: string;
  /** ISO date-time. */
  at: string;
  text: string;
}

/** "How did it go" after an interview date has passed (spec 11). */
export interface PlusTrackerDebrief {
  /** The interview this is about (PlusTrackerInterview.id). */
  interviewId: string;
  /** ISO date-time the debrief was saved. */
  at: string;
  feeling?: 'good' | 'mixed' | 'bad';
  notes: string;
  thankYouSentAt?: string;
}

/** What the user entered from one offer (spec 6.26). Everything optional: a half-filled offer still shows. */
export interface PlusOfferDetails {
  /** Gross EUR per month. */
  monthlyGross?: number;
  /** Total holiday days per year including the statutory ones, as stated in the offer. */
  holidayDays?: number;
  /** Free text, comma or newline separated. */
  benefits?: string;
  /** Office days per week (0 = fully remote). */
  officeDaysPerWeek?: number;
  /** Trial period (koeaika) in months. */
  trialMonths?: number;
  /** Home municipality for the net-pay estimate; absent: the national average rate. */
  municipality?: string;
  /** YYYY-MM-DD: the employer's deadline for an answer, when the offer states one (drives the offer deadline reminders). */
  replyDeadline?: string;
  /** ISO date-time the offer was entered or last edited. */
  enteredAt: string;
}

export interface PlusTrackerData {
  /** YYYY-MM-DD: the follow-up reminder date the user chose. */
  followUpAt?: string;
  interviews?: PlusTrackerInterview[];
  contact?: PlusTrackerContact;
  notes?: PlusTrackerNote[];
  debriefs?: PlusTrackerDebrief[];
  offer?: PlusOfferDetails;
  /** Suggestion keys the user dismissed on the timeline ("followUp:2026-10-20"). */
  dismissed?: string[];
}

export const PLUS_TRACKER_LIMITS = {
  interviews: 6,
  notes: 40,
  debriefs: 6,
  dismissed: 40,
  noteChars: 1000,
  placeChars: 160,
  textChars: 600,
  benefitsChars: 400,
} as const;

// ---------------------------------------------------------------------------------------------
// Saved-job watch (6.2) and apply-first (6.21)
// ---------------------------------------------------------------------------------------------

export type WatchSignalKind =
  | 'closes_soon' // deadline within 48 h
  | 'closes_tomorrow'
  | 'no_deadline_lifetime' // no deadline: typical lifetime vs days up
  | 'taken_down_after_applied'
  | 'posted_again';

export interface WatchSignal {
  kind: WatchSignalKind;
  jobId: string;
  /** Rank for "Up next" and the notification order (lower = more urgent). */
  urgency: number;
  /** Whole hours to the deadline (closes_*). */
  hoursLeft?: number;
  /** no_deadline_lifetime: typical days an ad for this role stays up, and days this one has been up. */
  typicalDays?: number;
  daysUp?: number;
  /** taken_down_after_applied: whole days between applying and the takedown. */
  daysAfterApplied?: number;
  /** posted_again: the new listing, when known. */
  repostJobId?: string;
  /** ISO date-time of the event the signal rests on (takedown, repost first seen, deadline). */
  at?: string;
  /** Basis for typicalDays (never shown below PLUS_MIN_SAMPLE closed ads). */
  basis?: PlusBasis;
}

/** What the server learned about one tracked job's listing. */
export interface WatchListing {
  isActive: boolean;
  /** ms epoch. */
  firstSeen?: number;
  lastSeen?: number;
  deactivatedAt?: number;
  deactivationReason?: string;
  /** ms epoch. */
  deadline?: number;
  /** A newer listing that links back to this job via likelyRepostOfJobId. */
  repostedAs?: { jobId: string; firstSeen?: number };
  roleSlug?: string;
}

export interface WatchTimeOpen {
  medianDays: number;
  p25Days: number;
  p75Days: number;
  basis: PlusBasis;
}

export interface WatchJobContext {
  jobId: string;
  stage: 'saved' | 'applied';
  listing: WatchListing | null;
  timeOpen: WatchTimeOpen | null;
}

export interface WatchBundle {
  /** Saved jobs the watch covers vs saved jobs the user has (count cap `savedJobWatch`). Absent when no cap applies. */
  watchCap?: { limit: number; saved: number };
  generatedAt: string;
  jobs: Record<string, WatchJobContext>;
  signals: Record<string, WatchSignal[]>;
}

export interface ApplyFirstItem {
  jobId: string;
  /** 1 = apply first. */
  rank: number;
  /** 0..1 through the usual lifetime (or to the deadline); above 1 means past the median. */
  progress: number;
  reason:
    | { kind: 'deadline'; hoursLeft: number }
    | { kind: 'lifetime'; day: number; medianDays: number; basis: PlusBasis }
    | { kind: 'unknown' };
}

// ---------------------------------------------------------------------------------------------
// Timeline (6.24)
// ---------------------------------------------------------------------------------------------

export type TimelineEventKind =
  | 'saved'
  | 'applied'
  | 'stage'
  | 'employer_email'
  | 'listing_change'
  | 'interview'
  | 'note'
  | 'reminder'
  | 'debrief';

export interface TimelineEvent {
  id: string;
  kind: TimelineEventKind;
  /** ISO date-time. */
  at: string;
  /** Where the event comes from, shown under it ("Your tracker", "Application address"). */
  source: 'tracker' | 'application_mail' | 'listing' | 'you';
  /** Machine key the UI turns into a localised sentence. */
  code: string;
  params?: Record<string, string | number | null>;
  /** Free text from the user or an employer email excerpt. */
  text?: string;
  /** Employer email that was filed but not read (weekly allowance used up): the timeline says so. */
  notRead?: ApplicationMailNotRead;
}

/** What an employer email looks like to the timeline (the Applying area's store feeds this). */
export interface EmployerEmailEvent {
  id: string;
  /** ISO date-time received. */
  receivedAt: string;
  fromName?: string;
  fromOrg?: string;
  subject?: string;
  /** Short excerpt, already stripped of quoted history. */
  excerpt?: string;
  classification: 'received' | 'info_request' | 'interview_invite' | 'rejection' | 'offer' | 'other';
  /** Interview details the classifier extracted, ISO date-time. */
  interviewAt?: string;
  interviewPlace?: string;
  /** True once the user replied. */
  answered?: boolean;
  /** Set when the email was filed but not read because the weekly classified-email allowance ran out. */
  notRead?: ApplicationMailNotRead;
}

export type NextStepKind =
  | 'reply_info_request'
  | 'prepare_interview'
  | 'debrief'
  | 'follow_up'
  | 'ask_feedback'
  | 'similar_jobs'
  | 'answer_offer'
  | 'compare_offers';

export interface NextStep {
  kind: NextStepKind;
  /** Stable key for dismissing ("follow_up:2026-10-20"). */
  key: string;
  params?: Record<string, string | number | null>;
}

export interface StatusLine {
  code:
    | 'saved'
    | 'applied_waiting'
    | 'applied_quiet'
    | 'invite_waiting'
    | 'info_request'
    | 'interview_upcoming'
    | 'interview_done'
    | 'offer'
    | 'rejected'
    | 'hired'
    | 'taken_down'
    | 'posted_again';
  params?: Record<string, string | number | null>;
}

export interface SimilarJobLink {
  id: string;
  title: string;
  companyName: string | null;
  companyId: string | null;
}

export interface TimelineBundle {
  jobId: string;
  status: StatusLine;
  events: TimelineEvent[];
  nextSteps: NextStep[];
  /** Three open jobs like this one, only after a rejection. */
  similarJobs: SimilarJobLink[];
  /** False until the Applying area's inbox exists; the UI then says where emails will show up. */
  employerEmailsAvailable: boolean;
}

export type DraftKind = 'followUp' | 'feedback' | 'thankYou';

export interface DraftResult {
  kind: DraftKind;
  locale: PlusLocale;
  subject: string;
  body: string;
}

// ---------------------------------------------------------------------------------------------
// Offer comparison (6.26) and negotiation (6.5)
// ---------------------------------------------------------------------------------------------

export type MarketVerdict = 'below' | 'near_low' | 'typical' | 'near_high' | 'above';

export interface MarketPosition {
  verdict: MarketVerdict;
  /** The cell the verdict rests on, EUR per month. */
  p25: number;
  median: number;
  p75: number;
  scope: 'level_region' | 'level' | 'region' | 'finland';
  basis: PlusBasis;
}

export interface OfferColumn {
  jobId: string;
  title: string;
  companyName: string | null;
  companyId: string | null;
  grossMonthly: number | null;
  netMonthly: number | null;
  /** True when the net figure used the national average rate instead of a municipality. */
  netIsGeneric: boolean;
  market: MarketPosition | null;
  officeDaysPerWeek: number | null;
  holidayDays: number | null;
  benefits: string[];
  trialMonths: number | null;
  /** Employer Score letter only. */
  employerGrade: 'A' | 'B' | 'C' | 'D' | 'E' | null;
  notes: string;
  /** Where the job is, for the commute the client works out from the user's home address. */
  jobPoint: { lat: number; lng: number } | null;
  /** Where the ad's own pay range (not the offer) sits, when it stated one. */
  adRange: { min: number; max: number } | null;
  /** R3-13: whether the ad is fixed-term, and for how many months when it says. Null: the ad does not tell. */
  contract?: ContractFacts | null;
}

export interface OfferCompareResult {
  columns: OfferColumn[];
  /** Plain-language comparison, built only from the entered figures. */
  insights: Array<{ code: 'pay_gap' | 'holiday_gap' | 'benefit_gap'; params: Record<string, string | number> }>;
  /** Job id to open the negotiation assistant for (the lower paid offer), when there is a gap. */
  negotiateJobId: string | null;
  /** R3-13: what each fixed-term offer gives up against each offer with no fixed term. Empty when the pair does not exist. */
  fixedTerm?: FixedTermComparison[];
}

export interface NegotiationItem {
  key: 'base' | 'start_date' | 'holidays' | 'remote' | 'training' | 'benefits' | 'notice';
  /** What the ad says about it, or null when it says nothing. */
  fromAd: string | null;
  /** True when the ad already states something about this item. */
  adMentions: boolean;
}

export interface NegotiationAssessment {
  jobId: string | null;
  title: string;
  companyName: string | null;
  offerMonthly: number;
  market: MarketPosition | null;
  /** Position of the offer inside the market cell, 0..1 along p25..p75 (can leave the range). */
  adRange: { min: number; max: number } | null;
  /** The ask we would suggest, rounded, never above the ad's own maximum when one is stated. */
  suggestedAsk: number | null;
  /** Collective agreement the ad names, if any. No pay-table figures are computed. */
  collectiveAgreement: string | null;
  items: NegotiationItem[];
  scripts: Record<PlusLocale, string>;
  /** Every figure used, with its source, so the UI can cite each. */
  citations: Array<{ id: string; label: string; value: string }>;
}
